import type Phaser from 'phaser';
import uiSheet from '../../../../tile_assets/06_User_Interface_Elements/UI_16x16.png';

/**
 * The puff of dust a sprinting character kicks up behind it on every step: the expanding white
 * ring from LimeZu's Modern Interiors UI elements (assets-src/LICENSES.md), squashed flat onto
 * the floor.
 */

const SHEET_KEY = 'ui:16x16';
const ANIMATION_KEY = 'sprint:dust';
/** The UI sheet is 18 columns of 16×16 frames; row 7 is the white ring, 6 frames growing out. */
const SHEET_COLUMNS = 18;
const DUST_ROW = 7;
const DUST_FRAMES = 6;
const FIRST_FRAME = DUST_ROW * SHEET_COLUMNS;
/** 6 frames in 250 ms: the puff is gone by the time a sprinting step has been taken. */
const FRAME_RATE = 24;
/** The frame held briefly instead of the animation when motion is reduced. */
const STILL_FRAME = FIRST_FRAME + 2;
const STILL_MS = 150;
/** Twice the art's size across like the characters, but flat: a ring on the floor. */
const SCALE = { x: 2, y: 1 } as const;
const ALPHA = 0.8;

export function loadSprintDust(scene: Phaser.Scene): void {
  scene.load.spritesheet(SHEET_KEY, uiSheet, { frameWidth: 16, frameHeight: 16 });
}

/**
 * Puffs dust centred on (`x`, `y`) at `depth`. Does nothing if the sheet did not load; with
 * reduced motion the ring shows for a moment without growing.
 */
export function puffDust(
  scene: Phaser.Scene,
  x: number,
  y: number,
  depth: number,
  reducedMotion: boolean,
): void {
  if (!scene.textures.exists(SHEET_KEY)) return;
  if (!scene.anims.exists(ANIMATION_KEY)) {
    scene.anims.create({
      key: ANIMATION_KEY,
      frames: scene.anims.generateFrameNumbers(SHEET_KEY, {
        start: FIRST_FRAME,
        end: FIRST_FRAME + DUST_FRAMES - 1,
      }),
      frameRate: FRAME_RATE,
    });
  }
  const puff = scene.add
    .sprite(x, y, SHEET_KEY, STILL_FRAME)
    .setScale(SCALE.x, SCALE.y)
    .setDepth(depth)
    .setAlpha(ALPHA);
  if (reducedMotion) {
    scene.time.delayedCall(STILL_MS, () => puff.destroy());
    return;
  }
  // Phaser's animation events, by name: this module only imports Phaser's types.
  puff.once('animationcomplete', () => puff.destroy());
  puff.play(ANIMATION_KEY);
}
